/**
 * Configuration tab — mirrors the v4 "Tokeniser Assumptions" tab sections
 * plus per-model pricing (ModelPricing), PT sizing, runtime assumptions, and add-ons.
 * All values feed into estimates via per-request overrides (held in browser
 * session; not persisted to the server).
 */

import type { EstimationParams, ModelInfo } from '@tcc/shared';
import { useState } from 'react';
import { BRAND } from '../../brand';
import { PT_ROWS, RUNTIME_ROWS, SIZING_ROWS } from '../../constants';
import { CharTokenRow } from '../CharTokenRow/CharTokenRow';
import { DocumentRow } from '../DocumentRow/DocumentRow';
import { ModelOptions } from '../ModelOptions/ModelOptions';
import { ModelPricing } from '../ModelPricing/ModelPricing';
import { Row } from '../Row/Row';
import { Section } from '../Section/Section';
import { TableHeader } from '../TableHeader/TableHeader';

export interface ConfigProps {
	models: ModelInfo[];
	/** Server catalog defaults, for the pricing section's "Edited" markers and resets (defaults to `models`). */
	defaultModels?: ModelInfo[];
	params: EstimationParams;
	onModelsChange: (models: ModelInfo[]) => void;
	onParamsChange: (params: EstimationParams) => void;
	onReset: () => void;
}

export function Config({
	models,
	defaultModels = models,
	params,
	onModelsChange,
	onParamsChange,
	onReset,
}: ConfigProps) {
	const [selectedId, setSelectedId] = useState(models[0]?.modelId ?? '');
	const model = models.find((m) => m.modelId === selectedId) ?? models[0];
	const hasGoogleModels = models.some((m) => m.provider === 'google');

	const updateModel = (patch: Partial<ModelInfo>) =>
		onModelsChange(models.map((m) => (m.modelId === model?.modelId ? { ...m, ...patch } : m)));
	const updateWeights = (patch: Partial<ModelInfo['imageTokenWeights']>) =>
		updateModel({ imageTokenWeights: { ...model.imageTokenWeights, ...patch } });

	const setP = (patch: Partial<EstimationParams>) => onParamsChange({ ...params, ...patch });
	const setTxt = (patch: Partial<typeof params.textInputTokens>) =>
		setP({ textInputTokens: { ...params.textInputTokens, ...patch } });
	const setOut = (patch: Partial<typeof params.outputSizeBaseTokens>) =>
		setP({ outputSizeBaseTokens: { ...params.outputSizeBaseTokens, ...patch } });
	const setPerms = (patch: Partial<typeof params.sensitivityPermutations>) =>
		setP({ sensitivityPermutations: { ...params.sensitivityPermutations, ...patch } });
	const setPtSizing = (key: keyof typeof params.ptSizing, v: number) =>
		setP({ ptSizing: { ...params.ptSizing, [key]: { perSecondThroughputPerGsu: v } } });
	const setRuntime = (key: keyof typeof params.runtimeAssumptions, field: 'vcpuPerRps' | 'gibRamPerRps', v: number) =>
		setP({
			runtimeAssumptions: {
				...params.runtimeAssumptions,
				[key]: { ...params.runtimeAssumptions[key], [field]: v },
			},
		});
	const setSizingRange = (key: keyof typeof params.sizingRanges, field: 'min' | 'max', v: number) =>
		setP({
			sizingRanges: {
				...params.sizingRanges,
				[key]: { ...params.sizingRanges[key], [field]: Number.isFinite(v) ? Math.max(1, v) : 1 },
			},
		});

	return (
		<div className="sg">
			<div className="sc">
				<div className="tbar">
					<span className="wm">{BRAND.name}</span>
				</div>

				{/* §1 Content size ranges */}
				<Section
					badge="1"
					title="Content size ranges"
					sub="Min/max bounds for the step 3 'Estimate the typical size of each input' sliders, per content type."
				/>
				<div className="cfg-table">
					<div className="cfg-table-hdr">
						<span className="cfg-table-hdr-lbl">Content type</span>
						<span className="cfg-col-hdr">Min</span>
						<span className="cfg-col-hdr">Max</span>
					</div>
					{SIZING_ROWS.map(({ key, label }) => (
						<div key={key} className="cfg-table-row">
							<span className="cfg-row-lbl">{label}</span>
							<input
								type="number"
								min={1}
								step={1}
								value={params.sizingRanges[key].min}
								onChange={(e) => setSizingRange(key, 'min', Number(e.target.value))}
								aria-label={`${label} min`}
								className="cfg-input-w90"
							/>
							<input
								type="number"
								min={1}
								step={1}
								value={params.sizingRanges[key].max}
								onChange={(e) => setSizingRange(key, 'max', Number(e.target.value))}
								aria-label={`${label} max`}
								className="cfg-input-w90"
							/>
						</div>
					))}
				</div>

				{/* §2 Text sizing — input */}
				<Section
					badge="2"
					title="Text sizing — input"
					sub="Characters and tokens per text unit (v4 Tokeniser §1). Used for primary text input and additional context."
				/>
				<TableHeader cols={['Unit', 'Characters (≈ tokens)']} />
				<CharTokenRow
					label="One sentence"
					tokens={params.textInputTokens.sentence}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setTxt({ sentence: v })}
				/>
				<CharTokenRow
					label="A paragraph"
					tokens={params.textInputTokens.paragraph}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setTxt({ paragraph: v })}
				/>
				<CharTokenRow
					label="A page"
					tokens={params.textInputTokens.page}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setTxt({ page: v })}
				/>
				<DocumentRow
					label="A document"
					tokens={params.textInputTokens.document}
					pageTokens={params.textInputTokens.page}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setTxt({ document: v })}
				/>
				<Row label="Characters per token" value={params.bytesPerRagToken} onChange={() => {}} disabled />
				<Row
					label="System prompt baseline (tokens)"
					value={params.systemPromptBaselineTokens}
					onChange={(v) => setP({ systemPromptBaselineTokens: v })}
				/>

				{/* §3 Modality conversion */}
				<Section
					badge="3"
					title="Modality conversion"
					sub="Voice tokens per minute · Video with audio tokens per minute (v4 Tokeniser §2)."
				/>
				<Row
					label="Voice tokens per minute"
					value={params.tokensPerVoiceMinute}
					onChange={(v) => setP({ tokensPerVoiceMinute: v })}
				/>
				<Row
					label="Video with audio tokens per minute"
					value={params.tokensPerVideoMinute}
					onChange={(v) => setP({ tokensPerVideoMinute: v })}
				/>

				{/* §4 Image sizing */}
				<Section
					badge="4"
					title="Image sizing"
					sub="Approximate tokens per image by size band (v4 Tokeniser §3). Select a model to edit its image weights."
				/>
				<label className="flbl">
					Model
					<select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} className="cfg-model-select">
						<ModelOptions models={models} />
					</select>
				</label>
				{model && (
					<div className="cfg-model-table">
						<TableHeader cols={['Size band', 'Max size (px)', 'Tokens']} />
						{(
							[
								['small', 'Icon / thumbnail (≤384px)', 384],
								['medium', 'Small image (≤512px)', 512],
								['large', 'Standard image (≤1024px)', 1024],
								['hd', 'HD / screenshot (≤1920px)', 1920],
								['ultra', '4K / very large (≤99999px)', 99999],
							] as const
						).map(([key, label]) => (
							<Row
								key={key}
								label={label}
								value={model.imageTokenWeights[key]}
								onChange={(v) => updateWeights({ [key]: v })}
							/>
						))}
					</div>
				)}

				{/* §5 Output sizing */}
				<Section
					badge="5"
					title="Output sizing"
					sub="Base response size per output unit, entered as characters with the token equivalent shown (v4 Tokeniser §4)."
				/>
				<TableHeader cols={['Output unit', 'Characters (≈ tokens)']} />
				<CharTokenRow
					label="One sentence"
					tokens={params.outputSizeBaseTokens.sentence}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setOut({ sentence: v })}
				/>
				<CharTokenRow
					label="A paragraph"
					tokens={params.outputSizeBaseTokens.paragraph}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setOut({ paragraph: v })}
				/>
				<CharTokenRow
					label="A page"
					tokens={params.outputSizeBaseTokens.page}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setOut({ page: v })}
				/>
				<CharTokenRow
					label="A document"
					tokens={params.outputSizeBaseTokens.document}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setOut({ document: v })}
				/>

				{/* §6 Decision overhead */}
				<Section
					badge="6"
					title="Decision overhead"
					sub="Reasoning paragraph size (entered as characters, token equivalent shown) and decision-complexity permutations. Decision counts are entered per 100 runs."
				/>
				<TableHeader cols={['Paragraph type', 'Characters (≈ tokens)']} />
				<CharTokenRow
					label="Chain of Thought (CoT)"
					tokens={params.chainOfThoughtPerDecision}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setP({ chainOfThoughtPerDecision: v })}
				/>
				<CharTokenRow
					label="Rationale for decision"
					tokens={params.rationalePerDecision}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setP({ rationalePerDecision: v })}
				/>
				<CharTokenRow
					label="Decision outcome"
					tokens={params.perDecisionTokenCost}
					charsPerToken={params.bytesPerRagToken}
					onChange={(v) => setP({ perDecisionTokenCost: v })}
				/>
				<div className="cfg-decision-total">
					Total per decision:{' '}
					{params.chainOfThoughtPerDecision + params.rationalePerDecision + params.perDecisionTokenCost} tokens
				</div>
				<TableHeader cols={['Complexity', 'Permutations']} />
				<Row
					label="Simple — low-impact / routine"
					value={params.sensitivityPermutations.simple}
					step={1}
					onChange={(v) => setPerms({ simple: v })}
				/>
				<Row
					label="Complex — sensitive / high-impact"
					value={params.sensitivityPermutations.complex}
					step={1}
					onChange={(v) => setPerms({ complex: v })}
				/>

				{/* §V Volumetrics basis */}
				<Section
					badge="V"
					title="Volumetrics basis"
					sub="Days used on the Input sizing step to convert the daily volume into monthly and yearly totals. Monthly = per day × days-in-month; yearly = per day × days-in-year. Defaults: 365/12 (≈30.42) days per month, 365 days per year."
				/>
				<Row
					label="Days in month"
					step={0.01}
					value={params.daysPerMonth}
					onChange={(v) => setP({ daysPerMonth: v })}
				/>
				<Row label="Days in year" step={1} value={params.daysPerYear} onChange={(v) => setP({ daysPerYear: v })} />

				{/* §£ Model pricing — per-model rates across tiers and context bands, plus FX */}
				<ModelPricing
					models={models}
					defaultModels={defaultModels}
					params={params}
					onModelsChange={onModelsChange}
					onParamsChange={onParamsChange}
				/>

				{/* §PT Provisioned Throughput sizing — Gemini-only, so shown only when the catalog has Google models. */}
				{hasGoogleModels && (
					<>
						<Section
							badge="PT"
							title="PT Sizing — Provisioned Throughput"
							sub="Requests per second of throughput per GSU, per Gemini model (v4 PT Sizing tab)."
						/>
						<Row
							label="PT cost per GSU per month (USD)"
							step={10}
							value={params.ptCostPerGsuPerMonth}
							onChange={(v) => setP({ ptCostPerGsuPerMonth: v })}
						/>
						<div className="cfg-subsection">
							<TableHeader cols={['Model', 'Requests per second per GSU']} />
							{PT_ROWS.map(({ key, label }) => (
								<Row
									key={key}
									label={label}
									value={params.ptSizing[key].perSecondThroughputPerGsu}
									onChange={(v) => setPtSizing(key, v)}
								/>
							))}
						</div>
					</>
				)}

				{/* §R Runtime assumptions */}
				<Section
					badge="R"
					title="Runtime Assumptions"
					sub="vCPU and GiB RAM required per request per second, per agent deployment style (v4 Runtime assumptions tab). Free tiers: 50 vCPU-hours/month, 100 GiB-hours/month."
				/>
				<div className="cfg-subsection--scroll">
					<TableHeader cols={['Agent type', 'vCPU / RPS', 'GiB RAM / RPS']} />
					{RUNTIME_ROWS.map(({ key, label }) => (
						<div key={key} className="cfg-table-row">
							<span className="cfg-row-lbl-wide">{label}</span>
							<input
								type="number"
								step={0.01}
								value={params.runtimeAssumptions[key].vcpuPerRps}
								onChange={(e) => setRuntime(key, 'vcpuPerRps', Number(e.target.value))}
								aria-label={`${label} vCPU per RPS`}
								className="cfg-input-flex"
							/>
							<input
								type="number"
								step={0.01}
								value={params.runtimeAssumptions[key].gibRamPerRps}
								onChange={(e) => setRuntime(key, 'gibRamPerRps', Number(e.target.value))}
								aria-label={`${label} GiB RAM per RPS`}
								className="cfg-input-flex"
							/>
						</div>
					))}
				</div>
				<Row
					label="vCPU rate ($ per vCPU-hour)"
					step={0.001}
					value={params.runtimeVcpuRateUsdPerHour}
					onChange={(v) => setP({ runtimeVcpuRateUsdPerHour: v })}
				/>
				<Row
					label="RAM rate ($ per GiB-hour)"
					step={0.001}
					value={params.runtimeRamRateUsdPerGibHour}
					onChange={(v) => setP({ runtimeRamRateUsdPerGibHour: v })}
				/>
				<div className="cfg-runtime-lbl">Agent runtime rates (Pricing tab B):</div>
				<Row
					label="Sessions $ per 1k events stored"
					step={0.01}
					value={params.agentSessionsRateUsd}
					onChange={(v) => setP({ agentSessionsRateUsd: v })}
				/>
				<Row
					label="Memory stored $ per 1k per month"
					step={0.01}
					value={params.agentMemoryStoredRateUsd}
					onChange={(v) => setP({ agentMemoryStoredRateUsd: v })}
				/>
				<Row
					label="Memory retrieved $ per 1k"
					step={0.01}
					value={params.agentMemoryRetrievedRateUsd}
					onChange={(v) => setP({ agentMemoryRetrievedRateUsd: v })}
				/>
				<div className="cfg-free-tier">
					Free tiers: 50 vCPU-hours/month · 100 GiB-hours/month · 1,000 memories retrieved/month
				</div>

				{/* §A Add-ons & Pricing */}
				<Section
					badge="A"
					title="Add-ons &amp; Pricing"
					sub="Model Armor token scanning cost and Apigee API management fees (v4 Pricing tab C)."
				/>
				<Row
					label="Model Armor $ per 1M tokens"
					step={0.01}
					value={params.modelArmorUsdPer1MTokens}
					onChange={(v) => setP({ modelArmorUsdPer1MTokens: v })}
				/>
				<Row
					label="Apigee base fee £/month"
					step={1}
					value={params.apigeeBaseFeeGbp}
					onChange={(v) => setP({ apigeeBaseFeeGbp: v })}
				/>
				<Row
					label="Apigee variable rate £ per 1M API calls"
					step={1}
					value={params.apigeeVariableRateGbpPer1MApiCalls}
					onChange={(v) => setP({ apigeeVariableRateGbpPer1MApiCalls: v })}
				/>

				<div className="note note-mt">
					Changes apply immediately to new estimates on the Estimator tab (browser session only — not persisted to the
					server).
				</div>
				<button type="button" className="btns" onClick={onReset}>
					Reset to server defaults
				</button>
			</div>
		</div>
	);
}
